import { updateModuleCache, cacheAllModuleFiles } from '@/store/sync/cachingUtils';
import { performInMemorySearch } from '@/components/search/searchStore';
import { SYNC_STATES } from '@/store/sync/sync';
import storeService from '@/services/storeService';
import IN_MEMORY_DB from './storeNonReactive';
import fahrdienstApi from './fahrdienstApi';
import dataUtils from '@/common/dataUtils';
import moduleId from './moduleId';
import moduleIndex from './index';
import fahrdienstUtil from './fahrdienstUtil';
import favoriteService from '@/services/favoritesService';
import dateUtils from '@/common/dateUtils';
import readService from '@/services/readService';
import acknowledgeService from '../../services/acknowledgeService';

const ROOT = { root: true };

const state = {
  // Data - all other arrays are stored in non-reactive store (=object), which is faster because no watchers are getting attached
  fahrdienstDuties: [],
  fahrdienstDutiesById: {},

  // Fahrdienst_files comment:
  // Both fields "dienst" and "linie" contain comma separated list of fahrdienst_attributes.value field (that is the name and not the id!!!!!)
  files: [], // actually non-reactive however the attached userStates require it to treat a reactive property

  filesById: {}, // same here

  userState: [],

  // View State
  // dutyFiles: [],
  displayedDocument: null,
  unreadCountsByInstance: {},
  selectedDate: new Date(),
  isLoaded: false, // true indicates, data has been loaded (at least one sync process)
  fahrdienstTimetableDayHours: null,
};

const getters = {
  // },
  /** ------------------------------------------------------------------------------------------------------------------
   * Module Implementation for unread badge counts by instance.
   * IMPLEMENT this getter to enable unread counter badges
   * -------------------------------------------------------------------------------------------------------------------
   */
  getUnreadCountByModuleInstance: (state) => (instance) => {
    return state.unreadCountsByInstance[instance];
  },

  getFilteredFahrdienstFiles: (state) => {
    let { selectedDate, selectedDuty, selectedLine } = state;

    let filtered = fahrdienstUtil.filterByDutyLineAndDate({
      fahrdienstFiles: state.files,
      instance: state.instance,
      selectedDateIsoString: selectedDate,
      selectedDuty,
      selectedLine,
    });
    // console.debug('filtered', JSON.stringify(filtered, null, 2));
    return filtered;
  },
  fahrdienstTimetableDayHours: (state) => state.fahrdienstTimetableDayHours,
};

const mutations = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for UPDATING Module counters
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  countModuleItems(state) {
    // count all unread files (not duty specific).
    // ValidFrom to is taken into account but not day type and special calendar
    // console.debug('countModuleItems Fahrdienst', state.dutyFiles.length);
    // find out type id of non duty-specific files
    console.debug('infoFileTypeByInstance', IN_MEMORY_DB.infoFileTypeByInstance);
    let unreadCountsByInstance = {};
    const now = new Date();
    const toDateOnly = (date) => {
      const d = new Date(date);
      d.setHours(0, 0, 0, 0);
      return d;
    };
    const today = toDateOnly(now);
    state.files
      .filter((f) => f.typeIdFk === IN_MEMORY_DB.infoFileTypeByInstance[f.inst])
      .forEach((f) => {
        // console.debug('Fahrdienst File dateread', f.id, f.title, f._userState.dateread, readService.isUnread(f), acknowledgeService.isToAcknowledge(f));
        const von = toDateOnly(f.anzeige_von);
        const bis = f.anzeige_bis ? toDateOnly(f.anzeige_bis) : null;
        const validTimeFrame = von <= today && (bis === null || today <= bis);
        if (validTimeFrame && (readService.isUnread(f) || acknowledgeService.isToAcknowledge(f))) {
          unreadCountsByInstance[f.inst] = Number(unreadCountsByInstance[f.inst] || 0) + 1;
        }
      });
    state.unreadCountsByInstance = unreadCountsByInstance;
  },
  setData(state, data) {
    storeService.saveDataToInMemoryStore(moduleId, state, IN_MEMORY_DB, data);
    state.fahrdienstDuties = data.fahrdienstDuties;
    state.fahrdienstDutiesById = data.fahrdienstDutiesById;

    /*
     * Find missing duties which are assigned to files but are not present in fahrdienst_attr
     * This can happen if for example an Adapter directly writes duties to fahrdienst files.
     */

    // get all duties which are asigned to files
    const fileDuties = [];

    data.files.forEach((f) => {
      if (f.dienst) {
        const duties = f.dienst.split(',').map((d) => d.trim());
        duties.forEach((d) => {
          if (!fileDuties.includes(d)) {
            fileDuties.push({ inst: f.inst, title: d });
          }
        });
      }
    });

    // get all existing duties from fahrdienst_attr
    const attrDuties = data.fahrdienstDuties.map((d) => d.title.trim());

    // caluclate intersection
    const missingDuties = fileDuties.filter((x) => !attrDuties.includes(x.title));

    // add the missing duties to the state and assign them an id which does not colide with existing duty ids.
    if (missingDuties.length > 0) {
      let dutyId = 1000;
      missingDuties.forEach((d) => {
        state.fahrdienstDuties.push({
          id: dutyId,
          inst: d.inst,
          title: d.title,
        });
        state.fahrdienstDutiesById[dutyId] = {
          id: dutyId,
          inst: d.inst,
          title: d.title,
        };
        dutyId++;
      });
    }

    /*
    // commented out because it replaces already existing duties with file names

    const currentDutiesNameMap = data.fahrdienstDuties.reduce((acc, item) => {
      if (item && item.title) {
        acc[item.title] = true;
      }
      return acc;
    }, {});

    const absentDutiesAsFilenames = data.files
      .filter((item) => !currentDutiesNameMap[item.title])
      .map((item) => {
        return {
          id: item.sort,
          title: item.title,
          inst: item.inst,
        };
      });
    
    state.fahrdienstDuties = [...data.fahrdienstDuties, ...absentDutiesAsFilenames];
    state.fahrdienstDutiesById = absentDutiesAsFilenames.reduce((acc, item) => {
      acc[item.id] = item;
      return acc;
    }, data.fahrdienstDutiesById);
    */

    state.files = data.files;
    state.filesById = data.filesById;
    state.userState = data.userState;

    state.isLoaded = true;
    state.fahrdienstTimetableDayHours = data.fahrdienstTimetableDayHours;
  },

  setDisplayedDocument: (state, displayedDocument) => {
    console.debug('setDisplayedDocument', displayedDocument.id);
    state.displayedDocument = displayedDocument;
  },

  setSelectedLine: (state, { line }) => (state.selectedLine = line),
};

const actions = {
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION for IN-MEMORY SEARCH
   * IMPLEMENT this getter to enable module in-memory search
   * Call the action in search/searchStore (search for "Module in-memory searches" there)
   * Search all documents containing all of the space separated search words in state.filterCriteria
   * ------------------------------------------------------------------------------------------------------------------ *
   */
  searchModuleInMemoryContainingSearchTerm({ state }, { filterCriteria, options }) {
    return performInMemorySearch({
      moduleId,
      items: state.files,
      titleSearchAttr: 'title',
      contentSearchAttr: null,
      searchCriteriaArray: filterCriteria,
      options,
    });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Updates the browsers cache storage oevpad-api/modules/{moduleId} with the exact same data structure as received by the module sync api call!
   * Reason: This mutation MUST be called whenever data has changed, e.g. user_state data. It assures that the application cache is up-to-date
   *         when the user synchronized/updates in offline mode.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async updateModuleApplicationCache({ state }) {
    // const { files, fahrdienstTypes, userState, verkehrsPeriodenKalender, reportFiles } = state;
    //   const { files, userState } = state;
    //
    //   const {
    //     fahrdienstDuties,
    //     fahrdienstDutiesById,
    //     fahrdienstTypes,
    //
    //     verkehrsPeriodenKalender,
    //     additionalFahrdienstDays,
    //     cancelledFahrdienstDays,
    //     infoFileTypeByInstance,
    //     fahrdienstLines,
    //     reportFiles,
    //   } = IN_MEMORY_DB;
    //
    //   // Use the exact same object that is transmitted by the module controller, eg. fahrdienstController !!!!
    //   await updateModuleCache(moduleIndex.api, {
    //     fahrdienstDuties,
    //
    //     fahrdienstFiles: files,
    //     fahrdienstTypes,
    //     userState,
    //     fahrdienstLines,
    //     reportFiles,
    //     verkehrsPeriodenKalender,
    //     additionalFahrdienstDays,
    //     cancelledFahrdienstDays,
    //   });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeFiles({ commit, dispatch, state }) {
    await cacheAllModuleFiles({ files: state.files, moduleIndex, commit, dispatch });
  },

  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION to synchronize files
   * Dispatched in store/sync !!!
   * Synchronize module files, that is storing files into browsers application cache by simply loading files from server.
   * The service-worker will intercept all requests and put files into cache if not yet there.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async synchronizeData({ commit, dispatch, rootState, rootGetters }) {
    try {
      const response = await fahrdienstApi.getData();
      let {
        fahrdienstDuties,
        fahrdienstFiles: files,
        fahrdienstTypes,
        fahrdienstLines,
        userState,
        reportFiles,
        verkehrsPeriodenKalender,
        additionalFahrdienstDays,
        cancelledFahrdienstDays,
        fahrdienstTimetableDayHours,
      } = response.data;
      // create helper objects (by id or the like)
      const filesById = {};
      const fahrdienstDutiesById = dataUtils.buildIdMap(fahrdienstDuties, 'id');
      const userStateByFahrdienstFileId = dataUtils.buildIdMap(userState, 'fileIdFk');
      const reportFilesByFileIdFk = dataUtils.buildIdMap(reportFiles, 'fileIdFk');

      const infoFileTypeByInstance = {}; // typeIdFk of info (Zirkulare) files
      fahrdienstTypes.forEach((ft) => {
        if (ft.isFahrdienstType === 0) infoFileTypeByInstance[ft.inst] = ft.id;
      });

      let today = new Date();
      let dateYYYYMMDD = dateUtils.formatDateAsYYYYMMDD(today);
      files.forEach((f) => {
        filesById[f.id] = f;
        //enhance item with information used when searching
        let moduleConfig = rootGetters['configs/getModuleConfig']({
          moduleType: moduleIndex.moduleType,
          instance: f.inst,
        });

        // add calculated data used mainly for displaying in live-search
        f._displayLocation = '';
        if (!moduleConfig) {
          console.debug('no moduleConfig found for', moduleIndex.moduleType, f.inst);
        } else {
          f._moduleTitle = moduleConfig.title;
        }

        f._moduleId = moduleId;

        f._path = `/fahrdienst/${f.inst}/${dateYYYYMMDD}/${f.id}/docOnly`;

        // Create a userState object in case it does not exist
        // IMPORTANT: Be sure to set all properties which can be changed and should be watched!!!!!
        f._userState = userStateByFahrdienstFileId[f.id]
          ? userStateByFahrdienstFileId[f.id]
          : {
              dateread: null,
              favorite: 0,
              dateconfirmed: null,
              fileIdFk: f.id,
            };
        f._userState._module = 'fahrdienst';
        f._userState._type = 'fahrdienstUserState';

        favoriteService.assertFavoriteIsSet(f);
      });
      favoriteService.addFavoriteStatesFromLocalStorage({ moduleId, items: files });

      commit('setData', {
        filesById,
        // userStateByFahrdienstFileId,

        fahrdienstDuties,
        fahrdienstDutiesById,
        files,
        userState,

        // static data
        fahrdienstTypes,
        verkehrsPeriodenKalender,
        additionalFahrdienstDays,
        cancelledFahrdienstDays,
        infoFileTypeByInstance,
        fahrdienstLines,
        reportFiles,
        fahrdienstTimetableDayHours,
      });
    } catch (err) {
      console.error(err);
      commit('sync/setSyncState', { moduleId, syncState: SYNC_STATES.FAILURE, msg: err }, ROOT);
      throw err;
    }
  },
  /**
   * ------------------------------------------------------------------------------------------------------------------
   * MANDATORY MODULE IMPLEMENTATION
   * Dispatched in store/sync !!!
   * Gets dispatched after all sync processes have been completed.
   * This is the place to implement any post sync processes.
   * ------------------------------------------------------------------------------------------------------------------
   */
  async onSyncComplete({ commit, dispatch, rootState, rootGetters }) {
    // Implement this method if necessary
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations,
};
